import React, { useCallback } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import MultiStepForm from 'shared/components/MultiStepForm';
import Modal from 'shared/components/Modal';
import { WORKFLOWS_PATH } from 'shared/constants/routePaths';
import { createWorkflow } from 'services/workflow/actionCreators';
import { getIsCreatingWorkflowFlag } from 'services/workflow/getters';
import { getWorkflowList } from 'services/workflow/getters';

import WorkflowDetails from '../common/steps/WorkflowDetails';
import workflowDetailsValidator from '../common/steps/WorkflowDetails/validator';

import SourceSelection from '../common/steps/SourceSelection';
import SourceNavContent from '../common/navigation-content/SourceNavContent';
import sourceValidator from '../common/steps/SourceSelection/validator';

import TransformationSelection from '../common/steps/TransformationSelection';
import TransformationNavContent from '../common/navigation-content/TransformationNavContent';
import transformationValidator from '../common/steps/TransformationSelection/validator';

import DestinationSelection from '../common/steps/DestinationSelection';
import DestinationNavContent from '../common/navigation-content/DestinationNavContent';
import destinationValidator from '../common/steps/DestinationSelection/validator';
import WorkflowDetailsNavContent from '../common/navigation-content/WorkflowDetailsNavContent';
import { WorkflowType } from 'shared/types/workflow';

const MODAL_NAME = 'create-workflow-modal-form';

const MODAL_OVERLAY_CLASS_NAME = 'create-workflow-form__modal-overlay';
const MODAL_CONTAINER_CLASS_NAME = 'create-workflow-form__modal-container';
const MODAL_CLOSE_ICON_CLASS_NAME = 'create-workflow-form__modal-close-icon';

const StyledModal = styled(Modal)`
  width: 100%;
  height: 100%;

  & {
    .${MODAL_OVERLAY_CLASS_NAME} {
      padding-right: 0;
      padding-bottom: 0;
      padding-left: 0;
    }

    .${MODAL_CONTAINER_CLASS_NAME} {
      width: 100%;
      height: 100%;
      padding: 0;
    }

    .${MODAL_CLOSE_ICON_CLASS_NAME} {
      top: 3.166666667rem;
      right: 2.333333333rem;
    }
  }
`;

const CreateWorkflowForm = () => {
  const dispatch = useDispatch();
  const isCreatingWorkflow = useSelector(getIsCreatingWorkflowFlag);
  const workflowList = useSelector(getWorkflowList);
  const history = useHistory();

  const handleClose = useCallback(() => {
    history.push(WORKFLOWS_PATH);
  }, []);

  const handleSubmit = useCallback((values) => {
    dispatch(createWorkflow(values));
  }, []);

  return (
    <StyledModal
      isOpen={true}
      onClose={handleClose}
      name={MODAL_NAME}
      overlayClassName={MODAL_OVERLAY_CLASS_NAME}
      containerClassName={MODAL_CONTAINER_CLASS_NAME}
      closeIconClassName={MODAL_CLOSE_ICON_CLASS_NAME}
    >
      <MultiStepForm
        steps={[{
          name: 'details',
          subtitle: 'Enter a name for your Workflow and select the Workflow Type.',
          component: WorkflowDetails,
          nextButtonLabel: 'Select Sources',
          navContentComponent: WorkflowDetailsNavContent,
          validator: (values) => workflowDetailsValidator(values, workflowList),
          initialValues: {
            type: WorkflowType.INDICATOR_PRIORITIZATION,
          },
        }, {
          name: 'sources',
          title: 'Select Sources',
          subtitle: 'Select from the list below the intel sources you want to use for Indicator Prioritization.',
          component: SourceSelection,
          nextButtonLabel: 'Select Transformations',
          navContentComponent: SourceNavContent,
          initialValues: [],
          validator: sourceValidator,
        }, {
          name: 'transformations',
          title: 'Select Transformations',
          subtitle: 'Filter your prioritized indicators by score and type and exclude Safelist terms to create a vetted list into your destination.',
          component: TransformationSelection,
          nextButtonLabel: 'Select Destination',
          navContentComponent: TransformationNavContent,
          validator: transformationValidator,
        }, {
          name: 'destination',
          title: 'Select Destination',
          subtitle: 'Select an Enclave or Workflow App Destination for your vetted indicator list.',
          component: DestinationSelection,
          navContentComponent: DestinationNavContent,
          validator: destinationValidator,
          initialValues: '',
        }]}
        submitButtonLabel='Create Workflow'
        onSubmit={handleSubmit}
        isSubmitting={isCreatingWorkflow}
      />
    </StyledModal>
  );
};

export { MODAL_NAME };
export default CreateWorkflowForm;
